/**
 * meters/registry.ts — Meter Model Registry
 * ===========================================
 * Single source of truth for which column map to use per meter model, plus
 * header-based auto-detection for sheets whose SheetSource omits meterModel.
 *
 * TO ADD A NEW METER MODEL:
 *   1. Create src/meters/<model>.ts implementing MeterModel
 *   2. Add its code to MeterModelCode in types.ts
 *   3. Import and register it here (one line)
 *   Done.
 */

import type { MeterModel, MeterModelCode } from './types';
import { normalizeHeader } from './helpers';
import { LK4405 } from './lk4405';
import { LK4410 } from './lk4410';

const METER_REGISTRY: Partial<Record<MeterModelCode, MeterModel>> = {
  lk4405: LK4405,
  lk4410: LK4410,
};

/** Default model used when a sheet declares no model and auto-detect fails. */
export const DEFAULT_METER_MODEL: MeterModelCode = 'lk4405';

/** Returns the model for a code, or throws if unregistered. */
export function getMeterModel(code: MeterModelCode): MeterModel {
  const m = METER_REGISTRY[code];
  if (!m) {
    throw new Error(
      `[Cortex] No meter model registered for "${code}". ` +
      `Create src/meters/${code}.ts and register it in src/meters/registry.ts.`,
    );
  }
  return m;
}

/** All registered models (useful for admin listings / Settings). */
export function getAllMeterModels(): MeterModel[] {
  return Object.values(METER_REGISTRY) as MeterModel[];
}

/** Type-guard for a supported model code. */
export function isSupportedMeterModel(code: string): code is MeterModelCode {
  return code in METER_REGISTRY;
}

/**
 * Auto-detect the meter model from a sheet's header row.
 * Scores each model by how many of its signature headers are present and picks
 * the best match. Falls back to DEFAULT_METER_MODEL if nothing matches.
 *
 * Used only when a SheetSource does not declare `meterModel` — declaring it
 * explicitly in config is always preferred and skips detection.
 */
export function detectMeterModel(headerRow: string[]): MeterModelCode {
  const present = new Set(headerRow.map(normalizeHeader));

  let best: MeterModelCode = DEFAULT_METER_MODEL;
  let bestScore = -1;

  for (const model of getAllMeterModels()) {
    const score = model.signatureHeaders.reduce(
      (acc, h) => acc + (present.has(normalizeHeader(h)) ? 1 : 0),
      0,
    );
    // Require every signature header to be present to count as a positive match;
    // among full matches, prefer the one with more signature headers (more specific).
    const fullMatch = score === model.signatureHeaders.length;
    const effective = fullMatch ? score : score - 100; // heavily penalise partial
    if (effective > bestScore) {
      bestScore = effective;
      best = model.code;
    }
  }
  return best;
}

/** Resolve a model from an optional declared code, else detect from headers. */
export function resolveMeterModel(
  declared: MeterModelCode | undefined,
  headerRow: string[],
): MeterModel {
  if (declared && isSupportedMeterModel(declared)) return getMeterModel(declared);
  return getMeterModel(detectMeterModel(headerRow));
}
